/**
 * Obfuscate JWT so Network tab does not show a recognizable Bearer JWT.
 * This is transport obfuscation only — not a substitute for HTTPS / real secrets.
 * Must stay in sync with backend/src/auth/cstore-token.util.ts
 */

const PEPPER = 'CStore#OpaqueToken#v1!bptech-2026-x7Kq'

function pepperBytes(): number[] {
  return Array.from(new TextEncoder().encode(PEPPER))
}

function xorBytes(data: number[], key: number[]): number[] {
  return data.map((b, i) => b ^ key[i % key.length] ^ ((i * 31) & 0xff))
}

function toHex(bytes: number[]): string {
  let out = ''
  for (const b of bytes) {
    out += b.toString(16).padStart(2, '0')
  }
  return out
}

function fromHex(hex: string): number[] | null {
  if (!hex || hex.length % 2 !== 0 || /[^0-9a-f]/i.test(hex)) return null
  const out: number[] = []
  for (let i = 0; i < hex.length; i += 2) {
    out.push(Number.parseInt(hex.slice(i, i + 2), 16))
  }
  return out
}

/** Reverse every 8-char hex chunk so the string is harder to eyeball. */
function scrambleHex(hex: string): string {
  const chunks: string[] = []
  for (let i = 0; i < hex.length; i += 8) {
    chunks.push([...hex.slice(i, i + 8)].reverse().join(''))
  }
  return chunks.join('')
}

function unscrambleHex(hex: string): string {
  return scrambleHex(hex) // reverse-of-reverse on fixed chunk size
}

/**
 * Encode a raw JWT into a long opaque CstoreToken value.
 * Idempotent: if already `cst1.…`, returns as-is. Strips accidental `Bearer ` prefix.
 */
export function encodeCstoreToken(jwt: string): string {
  let raw = String(jwt || '').trim()
  if (!raw) return ''
  if (/^bearer\s+/i.test(raw)) raw = raw.replace(/^bearer\s+/i, '').trim()
  // Already opaque — never re-encode (login may return cst1.…)
  if (raw.startsWith('cst1.')) return raw
  const xored = xorBytes(Array.from(new TextEncoder().encode(raw)), pepperBytes())
  const out = `cst1.${scrambleHex(toHex(xored))}`
  // Hard guarantee: Network must never see a value starting with eyJ
  if (out.startsWith('eyJ') || !out.startsWith('cst1.')) {
    throw new Error('CstoreToken encode failed')
  }
  return out
}

/**
 * Decode an opaque CstoreToken back to the original JWT.
 */
export function decodeCstoreToken(encoded: string): string | null {
  const value = String(encoded || '').trim()
  if (!value) return null

  // Already a plain JWT (legacy / mobile)
  if (value.startsWith('eyJ')) return value

  if (!value.startsWith('cst1.')) return null
  const scrambled = value.slice(5)
  const hex = unscrambleHex(scrambled)
  const bytes = fromHex(hex)
  if (!bytes) return null

  try {
    const decoded = new TextDecoder().decode(Uint8Array.from(xorBytes(bytes, pepperBytes())))
    return decoded.startsWith('eyJ') ? decoded : null
  } catch {
    return null
  }
}
